import React, { Fragment } from 'react';
import { withRouter, Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Icon from '@material-ui/core/Icon';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Hidden from '@material-ui/core/Hidden';
import withWidth, { isWidthDown, isWidthUp } from '@material-ui/core/withWidth';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import map from 'lodash/map';
import { Userpilot } from 'userpilot';
import CraverLogo from '../../assets/img/Craver_Logo_horizontal.svg';
import { menuBarStyles, menuDrawerStyle } from '../../css/style';
import {
  isDeviceView, getSelectedOptions,
} from '../../utils/Functions';
import { menuBarSubMenus } from '../../data/Constants';
import { checkIsAuthorized, getPermittedActionName, checkIsLocationAdmin } from '../../utils/AuthorizationChecks';
import LockIcon from '@material-ui/icons/Lock';
import Dropdown from '../share/Dropdown';
import * as CompanyFeatures from '../../services/CompanyFeatures';
import * as MultiAccountAdminRedirects from '../../services/MultiAccountAdminRedirects';

const styles = menuBarStyles();
const SHIFTED_INDEX_AFTER_FIRST_OPTION = 2;

// TODO: consider moving the pages to a global file
const allPages = {
  Home: { path: '/', val: 0, icon: <Icon>home</Icon> },
  Menu: {
    path: '/menu',
    subPaths: [/categories/, /customizations/, /products/, /items/, /options/, /taxes/],
    val: 1,
    icon: <Icon>restaurant_menu</Icon>,
    hasSubMenu: true,
  },
  Orders: { path: '/orders', val: 2, icon: <Icon>shopping_cart</Icon> },
  Locations: { path: '/locations', val: 3, icon: <Icon>location_on</Icon> },
  Users: { path: '/users', val: 4, icon: <Icon>people</Icon> },
  Rewards: { path: '/tiers', val: 5, icon: <Icon>star_border</Icon> },
  Coupons: { path: '/coupons', val: 6, icon: <Icon>card_giftcard</Icon> },
  Reports: {
    path: '/reports', val: 7, icon: <Icon>equalizer</Icon>, hide: true,
  },
};

const locationAdminPages = {
  Menu: { path: '/menu', val: 0, icon: <Icon>restaurant_menu</Icon> },
  Orders: { path: '/orders', val: 1, icon: <Icon>shopping_cart</Icon> },
  Locations: { path: '/locations', val: 2, icon: <Icon>location_on</Icon> },
};

class MenuBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isDrawerOpen: !isDeviceView('md', props.width),
      isMenuSubMenuOpen: false,
      selectedOptionState: {
        id: '',
        name: '',
        value: '',
      },
      dropdownNamesOptions: [],
    };
  }

  componentDidMount() {
    const { admin } = this.props;
    let companyNames = [];
    // call getNameOrEmail and set a state variable
    if (admin) {
      companyNames = this.getCompaniesDropdownOptions();
      this.setState({
        dropdownNamesOptions: companyNames,
        selectedOptionState: companyNames[0],
      });
    }
  }

  componentDidUpdate = (prevProps) => {
    if (get(prevProps, 'location.pathname') != get(this.props, 'location.pathname')) {
      Userpilot.reload();
    }
    this.redirectToListResourceIfChangeCompany(prevProps);
  }

  /**
   * This component is outside the BrowserRouter, so it is missing match.params
   */
  getUrlResourceSegment = () => {
    const currentUrl = location.pathname;
    const [emptyString, resource] = currentUrl.split('/');
    return resource;
  }

  redirectToListResourceIfChangeCompany(prevProps) {
    const prevCompanyId = get(prevProps, 'admin.company.id');
    const newCompanyId = get(this.props, 'admin.company.id');
    if (prevCompanyId && newCompanyId !== prevCompanyId) {
      const newApiToken = get(this.props, 'admin.token');

      const resource = this.getUrlResourceSegment();
      const { actions, history } = this.props;
      const redirectArgs = {
        apiToken: newApiToken,
        actions,
        resource,
        history,
        locationStateParams: { fromCreateEditResource: true },
      };
      MultiAccountAdminRedirects.redirectToResourcePageWithRefresh(redirectArgs);
    }
  }

  handleClickSignOut() {
    const { actions } = this.props;
    localStorage.clear();
    sessionStorage.clear();
    actions.signoutUser();
  }

  renderMenuLink = (props, page, pageName) => {
    const { admin } = this.props;
    const path = this.isLockedFromReports(pageName, admin)
      ? '/unauthorized'
      : page.path;

    return (
      <Link
        to={path}
        {...props}
      />
    );
  }

  isLockedFromReports = (pageName, admin) => (!admin || (admin && pageName === 'Reports' && getPermittedActionName(admin, 'reports') === 'lock'));

  showCompaniesDropdown = () => {
    const {
      actions, apiToken,
    } = this.props;
    const { dropdownNamesOptions, selectedOptionState } = this.state;

    return (
      <Dropdown
        data={selectedOptionState}
        actions={actions}
        dropdownOptions={dropdownNamesOptions}
        dropdownType='admins'
        allowMultiple={false}
        apiToken={apiToken}
        style={styles.adminFormControl}
        onChange={event => this.onAdminChange(event, dropdownNamesOptions, false)}
      />
    );
  }

  showStaticCompany = () => {
    const { dropdownNamesOptions } = this.state;
    if (!Array.isArray(dropdownNamesOptions) || dropdownNamesOptions.length > 1) return null;
    const companyName = get(dropdownNamesOptions, '[0].name', '');
    if (!companyName) return null;
    return (
      <Typography style={styles.greetingText}>{companyName}</Typography>
    );
  }

  onAdminChange = async (event, dropdownOptions, allowMultiple) => {
    const { actions, admin } = this.props;
    const selectedOption = getSelectedOptions(event, dropdownOptions, allowMultiple);
    this.setState({
      selectedOptionState: {
        id: selectedOption.id,
        name: selectedOption.name,
        value: selectedOption.value,
      },
    });
    await actions.switchAdmin(admin, selectedOption);
    // If someone has clicked Remember Me, then new windows should prioritize reloading the signed in user
    if (!admin && localStorage.admin) {
      await actions.setAdmin(JSON.parse(localStorage.getItem('admin')));
      return;
    }
    // Places admin into the Redux Store so that deeply nested store-connected components can access it without explicit prop passing
    if (!admin && sessionStorage.admin) {
      await actions.setAdmin(JSON.parse(sessionStorage.getItem('admin')));
    }
  }

  getCompaniesDropdownOptions = () => {
    const { admin } = this.props;
    const { administeredCompanies, company } = admin;
    let initialCompany = {};
    let CompaniesList = [];
    if (company) {
      initialCompany = {
        id: 1,
        name: company.name,
        value: company.id,
      };
    }
    if (administeredCompanies) {
      const filteredList = administeredCompanies.filter(names => names.apiToken !== undefined);
      CompaniesList = filteredList.map((names, ind) => ({
        id: ind + SHIFTED_INDEX_AFTER_FIRST_OPTION,
        name: (names.company) && names.company.name,
        value: (names.company) && names.company.id,
      }));
    }
    CompaniesList && CompaniesList.unshift(initialCompany);
    return CompaniesList;
  }

  getNameOrEmail = () => {
    const { admin } = this.props;
    const { company } = admin;
    if (!company || !company.name) return admin.email;
    return company.name;
  }

  showAppropriateAdminPages = () => {
    const { admin } = this.props;
    const isLocationAdmin = checkIsLocationAdmin(admin);
    if (isLocationAdmin) return locationAdminPages;
    return allPages;
  }

  showExpandSubMenuToggle = (pageName, page) => {
    const icon = this.state[`is${pageName}SubMenuOpen`]
      ? 'expand_less'
      : 'chevron_right';
    const style = this.getSubmenuToggleStyle(page);
    return <Icon style={style}>{icon}</Icon>;
  };

  getSubmenuToggleStyle = (page) => {
    const { path, subPaths } = page;
    const currentUrl = this.props.location.pathname;
    const unSelectedStyle = styles.subMenuToggleIcon;
    const selectedStyle = styles.subMenuExpandToggleIcon;
    if (!path) return unSelectedStyle;
    if (!subPaths) return (currentUrl === path) && selectedStyle;
    if (Array.isArray(subPaths)) {
      if (path === currentUrl) return selectedStyle;
      let finalStyle = unSelectedStyle;
      for (const subPath of subPaths) {
        if (subPath.test(currentUrl)) {
          finalStyle = selectedStyle;
          break;
        }
      }
      return finalStyle;
    }
    return unSelectedStyle;
  }

  handleClick = (e, page, pageName) => {
    if (!page) return;
    const { admin, onClickLocked, width } = this.props;
    if (this.isLockedFromReports(pageName, admin)) {
      onClickLocked();
    }
    if (!page.hasSubMenu && isDeviceView('md', width)) {
      this.onToggleDrawer();
    }

    if (!page.hasSubMenu) return;

    this.setState({ [`is${pageName}SubMenuOpen`]: !this.state[`is${pageName}SubMenuOpen`] });
  }

  renderExpandableSubMenu = (page, pageName) => {
    const { admin } = this.props;
    const isSharedOptionsEnabled = CompanyFeatures.isSharedOptionsEnabled(admin);

    return (
      <Collapse
        in={this.state[`is${pageName}SubMenuOpen`]}
        className={this.getMenuItemBackgroundColor(page, pageName, true)}
      >
        <MenuList style={styles.subMenuMenuListPadding}>
          {map(menuBarSubMenus[pageName], (subMenuItem) => {
            const isSelected = subMenuItem.selectedTest.test(this.props.location.pathname);
            const subMenuItemTextStyle = isSelected ? styles.subMenuSelectedListItemTypography : styles.subMenuListItemTypography;
            if (subMenuItem.key === 'Options' && !isSharedOptionsEnabled) return null;
            return (
              <MenuItem
                key={subMenuItem.key}
                value={subMenuItem.val}
                component={props => this.renderMenuLink(props, subMenuItem)}
                style={styles.subMenuItem}
                onClick={() => isDeviceView('md', this.props.width) && this.onToggleDrawer()}
              >
                <ListItemText
                  style={styles.subMenuListItemText}
                  inset
                  primary={<Typography style={subMenuItemTextStyle}>{subMenuItem.key}</Typography>}
                />
              </MenuItem>
            );
          })}
        </MenuList>
      </Collapse>
    );
  }

  getMenuItemBackgroundColor = (page, pageName, isSubMenu) => {
    const { path, subPaths } = page;
    const currentUrl = this.props.location.pathname;
    const unSelectedStyle = isSubMenu ? this.props.classes.blackSubMenuCollapse : '#ffffff';
    const selectedStyle = isSubMenu ? this.props.classes.greySubMenuCollapse : '#f9ded4';
    if (!path) return unSelectedStyle;
    if (!subPaths) return (currentUrl === path) && selectedStyle;
    if (Array.isArray(subPaths)) {
      if (path === currentUrl) return selectedStyle;
      let finalStyle = unSelectedStyle;
      for (const subPath of subPaths) {
        if (subPath.test(currentUrl)) {
          finalStyle = selectedStyle;
          break;
        }
      }
      return finalStyle;
    }
    return unSelectedStyle;
  }

  getListIconContainerStyle = () => {
    if (this.state.isDrawerOpen) return styles.drawerOpenListIconContainer;
    return styles.drawerClosedListIconContainer;
  }

  getListIconStyle = (page) => {
    const currentUrl = this.props.location.pathname;
    const selectedStyle = styles.listItemIconSelected;
    const unSelectedStyle = styles.listItemIcon;
    const { path, subPaths } = page;
    if (!path) return unSelectedStyle;
    if (!subPaths) return (currentUrl === path) ? selectedStyle : unSelectedStyle;
    if (Array.isArray(subPaths)) {
      if (path === currentUrl) return selectedStyle;
      let finalStyle = unSelectedStyle;
      for (const subPath of subPaths) {
        if (subPath.test(currentUrl)) {
          finalStyle = selectedStyle;
          break;
        }
      }
      return finalStyle;
    }
    return unSelectedStyle;
  }

  getListItemTypography = (page) => {
    const currentUrl = this.props.location.pathname;
    const selectedStyle = styles.listItemTypographySelected;
    const unSelectedStyle = styles.listItemTypography;
    const { path, subPaths } = page;
    if (!path) return unSelectedStyle;
    if (!subPaths) return (currentUrl === path) ? selectedStyle : unSelectedStyle;
    if (Array.isArray(subPaths)) {
      if (path === currentUrl) return selectedStyle;
      let finalStyle = unSelectedStyle;
      for (const subPath of subPaths) {
        if (subPath.test(currentUrl)) {
          finalStyle = selectedStyle;
          break;
        }
      }
      return finalStyle;
    }
    return unSelectedStyle;
  }

  handleToggleMenuBarSize = () => {
    const newState = {};
    newState.isDrawerOpen = !this.state.isDrawerOpen;
    for (const key in menuBarSubMenus) {
      newState[`is${key}SubMenuOpen`] = false;
    }
    this.setState(newState);
  }

  showLockIcon = () => (
    <LockIcon style={styles.lockIcon} />
  )

  onToggleDrawer = () => this.setState({ isDrawerOpen: !this.state.isDrawerOpen });

  render() {
    const { classes, admin, width } = this.props;
    const pages = this.showAppropriateAdminPages();
    const isTablet = isDeviceView('md', width);

    const { dropdownNamesOptions } = this.state;
    const hasMultipleAccounts = Array.isArray(dropdownNamesOptions) && dropdownNamesOptions.length > 1;

    return (
      <Fragment>
        <Hidden lgUp>
          <IconButton
            className={classes.openDrawerIcon}
            onClick={this.onToggleDrawer}
          >
            <Icon className={classes.menuIcon}>menu</Icon>
          </IconButton>
        </Hidden>
        <Drawer
          anchor='left'
          PaperProps={styles.drawerPaperProps}
          open={this.state.isDrawerOpen}
          variant={isTablet ? 'temporary' : 'permanent'}
          onClose={this.onToggleDrawer}
          classes={{
            root: this.state.isDrawerOpen ? classes.drawerOpen : classes.drawerClose,
            paper: this.state.isDrawerOpen ? classes.drawerOpen : classes.drawerClose,
          }}
        >
          <div className="gradient" style={styles.colorGradient}>
            <img
              src={CraverLogo}
              alt="Craver Logo"
              style={{
                height: '70px',
                width: this.state.isDrawerOpen ? '150px' : '200px',
                paddingLeft: this.state.isDrawerOpen ? '0' : '150px',
              }}
            />
          </div>
          <MenuList style={styles.menuList}>
            {map(pages, (page, pageName) => {
              const resourceTitle = pageName === 'Rewards' ? 'tiers' : lowerCase(pageName);
              const isLocked = getPermittedActionName(admin, resourceTitle) === 'lock';

              // For locked items, we want to show their existence, but disable them, to entice
              if (!checkIsAuthorized(admin, resourceTitle, null, 'view') && !isLocked) return;
              return (
                <div style={styles.menuItemContainer} key={pageName}>
                  <MenuItem
                    value={page.val}
                    component={props => this.renderMenuLink(props, page, pageName)}
                    onClick={e => this.handleClick(e, page, pageName)}
                    style={{
                      ...styles.menuItem,
                      backgroundColor: this.getMenuItemBackgroundColor(page, pageName),
                      paddingLeft: '0',
                    }}
                  >
                    <div style={this.getListIconContainerStyle()}>
                      <ListItemIcon style={this.getListIconStyle(page)}>
                        {page.icon}
                      </ListItemIcon>
                    </div>
                    {this.state.isDrawerOpen && (
                      <React.Fragment>
                        <div style={styles.subMenuListItemTextFlexContainer}>
                          <ListItemText style={styles.listItemText} primary={<Typography style={this.getListItemTypography(page)}>{pageName}</Typography>} />
                        </div>
                        <div style={styles.subMenuExpandToggleFlexContainer}>
                          {this.isLockedFromReports(pageName, admin) && this.showLockIcon()}
                          {page.hasSubMenu ? this.showExpandSubMenuToggle(pageName, page) : ''}
                        </div>
                      </React.Fragment>
                    )}
                  </MenuItem>
                  {page.hasSubMenu && this.renderExpandableSubMenu(page, pageName)}
                </div>
              );
            })}
            {this.state.isDrawerOpen && hasMultipleAccounts && this.showCompaniesDropdown()}
          </MenuList>
          {!hasMultipleAccounts && this.showStaticCompany()}
          <IconButton
            style={styles.expandOrCollapseButton}
            onClick={this.handleToggleMenuBarSize}
          >
            <Icon>{this.state.isDrawerOpen ? 'keyboard_arrow_left' : 'keyboard_arrow_right' }</Icon>
          </IconButton>
          <Typography style={styles.openOrCloseText}>{ this.state.isDrawerOpen ? 'CLOSE' : 'OPEN' }</Typography>
          <Link to="/" style={styles.signoutBtn} onClick={() => this.handleClickSignOut()}>Sign out</Link>
        </Drawer>
      </Fragment>
    );
  }
}

MenuBar.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withStyles(menuDrawerStyle)(withWidth()(withRouter(MenuBar)));
